import { z } from "zod";

/**
 * Attractions (ADR-0016): four Townsy-run buildings whose ONE shared interior room hosts a game. The server decides
 * every outcome; the browser only sends intents (`venue.action`). Shared state goes to everyone in the room
 * (`venue.state`, at most 15 Hz or on change); a visitor's own chips and bets go to that visitor only
 * (`venue.private`); one-off notices (a win, a goal, a refusal) are `venue.event`.
 */
export const VenueKind = z.enum([
  "casino",
  "race_track",
  "sports_center",
  "truck_arena",
  // Sports Park stadiums (ADR-0044): live AI games to watch, predictions with play chips.
  "football_stadium",
  "soccer_stadium",
  "baseball_stadium",
  "basketball_arena",
  "hockey_arena",
]);
export type VenueKind = z.infer<typeof VenueKind>;

/**
 * Play chips (ADR-0016 #2, ADR-0024): worthless chips for the casino and the race track, never money, coins or credit.
 * Each visitor has ONE stack, shared by both venues and kept between visits; walking into either one you have your
 * stack, or this many when it is smaller (or there is none).
 */
export const PLAY_CHIPS_ON_ENTRY = 100;
/** The most play chips one stack can hold (kept or in play); winnings above it are not added. Integer chips only. */
export const PLAY_CHIPS_MAX = 1_000_000;
export const PLAY_CHIPS_NOTICE =
  "Play chips are just for fun and have no value: they can't be bought, cashed out, transferred or turned into coins or Townsy Credit. Your play chips are shared by the casino and Townsy Downs and kept for your next visit. If you have fewer than 100 when you walk in, you get 100. Playing needs an account (18+).";
/** Venues that hand out play chips (one stack per visitor, shared by all of them, ADR-0024; the stadiums' predictions, ADR-0044). */
export const ChipVenue = z.enum(["casino", "race_track", "football_stadium", "soccer_stadium", "baseball_stadium", "basketball_arena", "hockey_arena"]);
export type ChipVenue = z.infer<typeof ChipVenue>;
export const CHIP_VENUES: readonly VenueKind[] = ChipVenue.options;
/** Venues whose betting is behind VENUE_CHANCE_GAMES_ENABLED (ADR-0016 #6; the stadiums' predictions, ADR-0044). */
export const CHANCE_GAME_VENUES: readonly VenueKind[] = ["casino", "race_track", "football_stadium", "soccer_stadium", "baseball_stadium", "basketball_arena", "hockey_arena"];
/**
 * Per-venue room capacity (fans per room). Venues not listed hold the street's cap (ADR-0016); the stadiums hold 50
 * (ADR-0044 decision 4).
 */
export const VENUE_ROOM_CAPACITY: Readonly<Partial<Record<VenueKind, number>>> = {
  football_stadium: 50,
  soccer_stadium: 50,
  baseball_stadium: 50,
  basketball_arena: 50,
  hockey_arena: 50,
};
export const CHANCE_GAMES_OFF_MESSAGE = "Betting with play chips is switched off on this server. You can still look around, watch and chat.";

/**
 * Betting needs an account (ADR-0048; founder 2026-10-27: "Betting needs an account"): only a signed-up account stakes
 * play chips (the casino's games, Townsy Downs, the stadiums' predictions), so the 18+ sign-up (ADR-0047) covers all
 * betting. Guests can still walk in, watch, sit in the stands, cheer, use the bar and chat. The server refuses a guest's
 * wager with this code and message (`venue.event` "rejected"), and a guest is given no play-chip stack at all; the
 * overlays show the message with a link to the sign-up instead of the betting controls.
 */
export const ACCOUNT_REQUIRED_CODE = "ACCOUNT_REQUIRED";
export const ACCOUNT_REQUIRED_MESSAGE = "Sign up (18+) to play";

/** Client -> server. `action` is validated by the venue's own schema (casino.ts, racing.ts, sports.ts, trucks.ts). */
export const VenueActionMessage = z
  .object({
    venue: VenueKind,
    action: z.record(z.string(), z.unknown()),
  })
  .strict();
export type VenueActionMessage = z.infer<typeof VenueActionMessage>;

/** Server -> everyone in the room. `state` is the venue's shared state type (e.g. CasinoState). */
export const VenueStateMessage = z.object({
  venue: VenueKind,
  /** Increases with every broadcast of this room's game (the client ignores older ones). */
  seq: z.number().int().min(0),
  state: z.unknown(),
});
export type VenueStateMessage = z.infer<typeof VenueStateMessage>;

/** Server -> one visitor: their play chips (null where the venue has none) and their own game state. */
export const VenuePrivateMessage = z.object({
  venue: VenueKind,
  chips: z.number().int().min(0).nullable(),
  state: z.unknown(),
});
export type VenuePrivateMessage = z.infer<typeof VenuePrivateMessage>;

export const VenueEventType = z.enum(["rejected", "notice", "win", "result"]);
export type VenueEventType = z.infer<typeof VenueEventType>;

/** Server -> everyone or one visitor: a one-off notice. `rejected` answers an action that was refused. */
export const VenueEventMessage = z.object({
  venue: VenueKind,
  type: VenueEventType,
  /** Machine-readable reason for `rejected` (e.g. NOT_ENOUGH_CHIPS, TOO_FAR, BETTING_CLOSED, CHANCE_GAMES_OFF). */
  code: z.string().max(40).optional(),
  /** Plain sentence shown to the visitor. */
  message: z.string().max(200),
  data: z.unknown().optional(),
});
export type VenueEventMessage = z.infer<typeof VenueEventMessage>;
